import { useQueryClient } from '@tanstack/react-query';
import { Send, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODE_ROLE, type AgentConfigDTO, type ConversationDTO, type ConversationMode } from '@harness/shared';
import { Spinner } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import type { ConversationData } from '../../lib/events';
import { qk } from '../../lib/keys';
import { useIntegrations, type ProjectDetail } from '../../lib/queries';
import { toast, ui } from '../../lib/store';
import { modeMeta } from '../../lib/modes';
import { useCan, VIEWER_HINT } from '../../lib/role';

const drafts = new Map<string, string>();

export default function Composer({
  project,
  mode = 'feature',
  convId,
  busy,
  agents,
  narrow,
}: {
  project: ProjectDetail;
  /** Tickets and Analysis talk to one agent: no @mentions or /feature there. */
  mode?: ConversationMode;
  convId?: string;
  busy: boolean;
  agents: AgentConfigDTO[];
  narrow: boolean;
  onNeedConversation: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(() => drafts.get(convId ?? project.id) ?? '');
  const [sending, setSending] = useState(false);
  const [suggest, setSuggest] = useState<{ q: string; sel: number } | null>(null);
  const integrations = useIntegrations(project.id);
  const jiraKey = integrations.data?.jira?.projectKey;
  const def = agents.find((a) => a.isDefault) ?? agents[0];

  useEffect(() => {
    drafts.set(convId ?? project.id, text);
  }, [text, convId, project.id]);

  // Prefills from elsewhere (Request changes → "@planner ").
  useEffect(() => {
    const on = (e: Event) => {
      setText((e as CustomEvent<string>).detail);
      setTimeout(() => {
        ref.current?.focus();
        ref.current?.setSelectionRange(ref.current.value.length, ref.current.value.length);
      }, 30);
    };
    window.addEventListener('harness:prefill', on);
    return () => window.removeEventListener('harness:prefill', on);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 66)}px`;
  }, [text]);

  const send = async () => {
    const t = text.trim();
    if (!t || busy || sending) return;
    const feature = mode === 'feature' ? t.match(/^\/feature\b\s*([A-Z][A-Z0-9]+-\d+)?/i) : null;
    if (feature) {
      setText('');
      if (feature[1]) ui.set({ repoPick: { projectId: project.id, issueKey: feature[1].toUpperCase() } });
      else window.dispatchEvent(new CustomEvent('harness:ctx-tab', { detail: 'tasks' }));
      return;
    }
    setSending(true);
    try {
      let id = convId;
      if (!id) {
        const c = await api.post<ConversationDTO>(`/api/projects/${project.id}/conversations`, { mode });
        qc.setQueryData<ConversationDTO[]>(qk.conversations(project.id), (l) => [c, ...(l ?? [])]);
        id = c.id;
        drafts.delete(project.id);
        nav(`/p/${project.slug}/c/${c.id}`, { replace: true });
      }
      const r = await api.post<{ message: ConversationData['messages'][number] }>(`/api/conversations/${id}/messages`, { text: t });
      qc.setQueryData<ConversationData>(qk.conversation(id), (d) => (d && !d.messages.some((m) => m.id === r.message.id) ? { ...d, messages: [...d.messages, r.message] } : d));
      setText('');
      drafts.delete(id);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_model') {
        toast(e.message, 'error');
        nav(`/p/${project.slug}/settings/agents`);
      } else toast(errorText(e), 'error');
    } finally {
      setSending(false);
    }
  };

  const stop = async () => {
    if (!convId) return;
    await api.post(`/api/conversations/${convId}/stop`).catch((e) => toast(errorText(e), 'error'));
  };

  const insertMention = (handle: string) => {
    const el = ref.current;
    if (suggest && el) {
      const pos = el.selectionStart;
      const before = text.slice(0, pos).replace(/@([a-z0-9-]*)$/i, `@${handle} `);
      setText(before + text.slice(pos));
      setSuggest(null);
    } else setText((`@${handle} ` + text.replace(/^@\S+\s*/, '')).trimStart());
    setTimeout(() => ref.current?.focus(), 10);
  };

  const onChange = (v: string, caret: number) => {
    setText(v);
    const m = v.slice(0, caret).match(/(?:^|\s)@([a-z0-9-]*)$/i);
    setSuggest(m && agents.length > 1 ? { q: m[1]!.toLowerCase(), sel: 0 } : null);
  };
  const matches = suggest ? agents.filter((a) => a.handle.startsWith(suggest.q) || a.name.toLowerCase().startsWith(suggest.q)) : [];

  const can = useCan();
  const offline = project.server && project.server.status !== 'online';
  const placeholder = offline
    ? 'Server offline — you can still chat, but tools are blocked'
    : mode !== 'feature'
      ? modeMeta(mode).placeholder
    : `Message @${def?.handle ?? 'agent'}${agents.length > 1 ? ' · @ to address an agent' : ''}${jiraKey ? ` · /feature ${jiraKey}-123` : ''}`;

  if (!can.edit)
    return (
      <div className="composer-wrap">
        <div className="composer muted" style={{ padding: '14px 16px', fontSize: 14 }} role="note">
          {VIEWER_HINT}
        </div>
      </div>
    );

  return (
    <div className="composer-wrap">
      <div className="composer" style={{ position: 'relative' }}>
        {suggest && matches.length > 0 && (
          <div className="menu suggest" role="listbox">
            {matches.map((a, i) => (
              <button
                key={a.id}
                className="menu-item"
                data-active={i === suggest.sel}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(a.handle);
                }}
              >
                <span className="avatar accent" style={{ width: 22, height: 22, fontSize: 10 }}>
                  {a.name[0]}
                </span>
                <span style={{ flex: 1 }}>
                  @{a.handle} <span className="faint" style={{ fontSize: 12 }}>{a.role}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <textarea
          id="composer"
          ref={ref}
          rows={3}
          value={text}
          placeholder={placeholder}
          aria-label="Message"
          onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
          onKeyDown={(e) => {
            if (suggest && matches.length) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const d = e.key === 'ArrowDown' ? 1 : -1;
                setSuggest({ ...suggest, sel: (suggest.sel + d + matches.length) % matches.length });
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(matches[suggest.sel]!.handle);
                return;
              }
              if (e.key === 'Escape') {
                setSuggest(null);
                return;
              }
            }
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <div className="composer-bar">
          {mode === 'feature' && agents.map((a) => (
            <button key={a.id} className="mention" onClick={() => insertMention(a.handle)}>
              @{a.handle}
            </button>
          ))}
          {mode === 'feature' && (
            <button
              className="mention"
              onClick={() => {
                setText(`/feature ${jiraKey ? `${jiraKey}-` : ''}`);
                setTimeout(() => ref.current?.focus(), 10);
              }}
            >
              /feature
            </button>
          )}
          {mode !== 'feature' && <span className="composer-hint">Read-only: the {MODE_ROLE[mode]} reads the code but never changes it</span>}
          <span className="composer-send">
          {!narrow && <span className="composer-hint">Enter to send · Shift+Enter for a new line</span>}
          {busy ? (
            <button className="btn btn-secondary" onClick={stop} style={{ gap: 8 }}>
              <Square className="icon" />
              Stop
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => void send()} disabled={sending || !text.trim()} style={{ gap: 8 }}>
              Send
              {sending ? <Spinner /> : <Send className="icon" />}
            </button>
          )}
          </span>
        </div>
      </div>
    </div>
  );
}
